import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthProvider';
import { useLoginPrompt } from '../contexts/LoginPromptContext';

interface GuestGateProps {
  children: React.ReactNode;
  featureName: string;
}

export const GuestGate: React.FC<GuestGateProps> = ({ children, featureName }) => {
  const { user, supabaseUser, loading } = useAuth();
  const { openLoginPrompt } = useLoginPrompt();
  const navigate = useNavigate();

  const isAuthenticated = Boolean(user || supabaseUser);

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      openLoginPrompt(featureName);
    }
  }, [loading, isAuthenticated, featureName, openLoginPrompt]);

  if (loading) {
    return (
      <div className="min-h-[50vh] flex flex-col items-center justify-center space-y-3">
        <div className="w-8 h-8 rounded-full border-2 border-purple-500 border-t-transparent animate-spin" />
        <p className="text-slate-500 text-xs font-semibold">Carregando...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center p-6 max-w-md mx-auto animate-in fade-in zoom-in-95 duration-300">
        <div className="w-20 h-20 rounded-3xl bg-slate-800/80 border border-slate-700 flex items-center justify-center text-slate-300 text-3xl shadow-2xl shadow-black/40 mb-6">
          <i className="fas fa-lock"></i>
        </div>
        <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 bg-slate-800/80 px-3.5 py-1 rounded-full border border-slate-700 mb-3 inline-flex items-center gap-1.5">
          <i className="fas fa-shield-halved text-[9px]"></i>
          <span>Funcionalidade Restrita</span>
        </span>
        <h2 className="text-2xl font-black text-white mb-2">
          Login Necessário
        </h2>
        <p className="text-slate-400 text-sm mb-6 leading-relaxed">
          Você precisa estar conectado à sua conta para acessar <strong className="text-white bg-slate-800/80 px-2 py-0.5 rounded border border-white/5">{featureName}</strong>. Crie sua conta grátis ou faça login para continuar.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 w-full">
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-bold py-3 px-5 rounded-xl text-sm transition-all shadow-lg shadow-purple-600/20 flex items-center justify-center gap-2 cursor-pointer active:scale-95"
          >
            <i className="fas fa-arrow-right-to-bracket text-xs"></i>
            <span>Fazer Login</span>
          </button>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold py-3 px-5 rounded-xl text-sm transition-all border border-white/5 flex items-center justify-center gap-2 cursor-pointer active:scale-95"
          >
            <i className="fas fa-house text-xs"></i>
            <span>Ir para o Início</span>
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};
